import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { formatET } from "../lib/dateUtils";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import { getClientNavItems } from "../modules/clientNav";
import { api, formatApiErrorDetail } from "../lib/api";
import { SUPPORT_EMAIL } from "../constants/brand";

const STATUS_COLOR = {
  Pipeline: "text-igc-muted",
  Submitted: "text-igc-goldText",
  Underwriting: "text-igc-goldText",
  Approved: "text-igc-success",
  Funded: "text-igc-success",
  Declined: "text-igc-danger",
};

export default function ClientFundingPipelinePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/client/funding-pipeline/deals")
      .then((res) => setData(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const navItems = getClientNavItems(user?.unlocked_modules, user?.role);

  return (
    <DashboardLayout navItems={navItems} moduleName="Funding Pipeline" moduleBackPath="/client">
      <div data-testid="client-funding-pipeline-page" className="max-w-3xl">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-2" title="Track every business funding deal you're part of and the 20% commission owed on each.">Funding Pipeline</h1>
        <p className="text-igc-muted text-sm mb-8">Your funding deals and 20% commission on each — tracked separately from your referral commissions.</p>

        {!loading && data && (
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-igc-surface border border-igc-gold/15 p-5" data-testid="funding-pipeline-total-deal-value">
              <p className="text-igc-muted text-xs uppercase tracking-widest mb-2" title="The combined dollar value of every funding deal you're part of.">Total Deal Value</p>
              <p className="font-serif text-2xl text-igc-cream">${(data.total_deal_amount || 0).toLocaleString()}</p>
            </div>
            <div className="bg-igc-surface border border-igc-gold/15 p-5" data-testid="funding-pipeline-total-commission-earned">
              <p className="text-igc-muted text-xs uppercase tracking-widest mb-2" title="20% of your total deal value — your commission on these deals.">Commission Earned (20%)</p>
              <p className="font-serif text-2xl text-igc-success">${(data.total_commission || 0).toLocaleString()}</p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {!loading && data && data.deals.length === 0 && <p className="text-igc-muted text-sm" data-testid="funding-pipeline-empty-state">No funding deals on file yet.</p>}
          {data && data.deals.map((d) => (
            <motion.div key={d.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-igc-surface border border-igc-gold/15 p-5" data-testid={`funding-pipeline-deal-${d.id}`}>
              <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
                <div>
                  <p className="text-igc-cream text-sm">{d.deal_name}</p>
                  <p className="text-igc-muted text-xs">Deal value ${(d.deal_amount || 0).toLocaleString()} — Updated {formatET(d.updated_at, "MMM d, yyyy")}</p>
                </div>
                <div className="text-right">
                  <span className={`text-xs font-medium ${STATUS_COLOR[d.status] || "text-igc-muted"}`} data-testid={`funding-pipeline-deal-status-${d.id}`}>{d.status}</span>
                  <p className="text-igc-success text-sm mt-1" data-testid={`funding-pipeline-deal-commission-${d.id}`}>${(d.commission_amount || 0).toLocaleString()} commission</p>
                </div>
              </div>
              {(d.stage_history || []).length > 0 && (
                <div className="border-l-2 border-igc-gold/30 pl-4 pt-2" data-testid={`funding-pipeline-deal-history-${d.id}`}>
                  <p className="text-igc-muted text-[10px] uppercase tracking-widest mb-1.5">Deal Timeline</p>
                  {d.stage_history.map((h, i) => (
                    <p key={i} className="text-igc-muted text-xs">
                      <span className="text-igc-goldText">{h.status}</span> — {formatET(h.changed_at, "MMM d, yyyy h:mm a")}
                    </p>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <p className="text-igc-muted text-xs text-center mt-10">Questions? Contact us at {SUPPORT_EMAIL}</p>
      </div>
    </DashboardLayout>
  );
}
